













































































































































































































import { Component, Watch } from 'vue-property-decorator';
import '@/assets/scss/pages/app-todo.scss';
import { ListIcon, PlusIcon, GridIcon, XIcon, CalendarIcon } from 'vue-feather-icons';
import { mixins } from 'vue-class-component';
import { BaseMixin } from '@/mixins/BaseMixin';
import {
  ITag,
  ITask,
  ReorderTasksDto,
  TaskFilterDto,
  TaskPriority,
  TaskStatus,
  IUser,
} from 'admiral-core';
import { TaskApi } from '@/services/TaskApi';
import { FormatMixin } from '@/mixins/FormatMixin';
import CreateTaskModal from '@/views/pages/tasks2/CreateTaskModal.vue';
import EditTaskModal from '@/views/pages/tasks2/EditTaskModal.vue';
import TaskKanban from './TaskKanban.vue';
import TaskOverview from './TaskOverview.vue';
import TasksCalendar from './TasksCalendar.vue';
import TaskPriorityFilter from '@/components/tasks/TaskPriorityFilter.vue';
import TaskDueDateFilter from '@/components/tasks/TaskDueDateFilter.vue';
import TaskTagFilter from '@/components/tasks/TaskTagFilter.vue';
import TaskUserFilter from '@/components/tasks/TaskUserFilter.vue';
import { PreferencesAction } from '@/store/PreferencesModule';
import dayjs from 'dayjs';

/** Initial page size and step for COMPLETED / ARCHIVED on tasks2 (avoids huge first payload). */
const TASKS2_TERMINAL_PAGE_SIZE = 10;

/** Statuses loaded and shown on tasks2 (terminal columns use list limits on the API). */
const TASKS2_LIST_STATUSES: TaskStatus[] = [
  TaskStatus.TODO,
  TaskStatus.IN_PROGRESS,
  TaskStatus.IN_WAITING,
  TaskStatus.COMPLETED,
  TaskStatus.ARCHIVED,
];

@Component({
  name: 'Tasks',
  components: {
    CreateTaskModal,
    EditTaskModal,
    TaskKanban,
    TaskOverview,
    TasksCalendar,
    TaskPriorityFilter,
    TaskDueDateFilter,
    TaskTagFilter,
    TaskUserFilter,
    ListIcon,
    PlusIcon,
    GridIcon,
    XIcon,
    CalendarIcon,
  },
})
class Tasks extends mixins(BaseMixin, FormatMixin) {
  private static readonly DUE_FILTER_STORAGE_KEY = 'tasks2_due_filter';
  private static readonly PRIORITY_FILTER_STORAGE_KEY = 'tasks2_priority_filter';

  /**
   * Tracks whether we last loaded COMPLETED/ARCHIVED without API caps because {@link searchQuery}
   * was active — toggling this triggers a refetch so search sees terminal tasks beyond pagination.
   */
  private prevSearchUncappedTerminalFetch = false;

  filter: TaskFilterDto = {
    status: [...TASKS2_LIST_STATUSES],
  };

  tasks: ITask[] = [];
  /** True until the initial (or full) task list fetch finishes — avoids empty-state flash. */
  isLoading = true;
  /** Cumulative cap for COMPLETED rows from API (increased by “load more”). */
  completedListLimit = TASKS2_TERMINAL_PAGE_SIZE;
  isLoadingMoreCompleted = false;
  /** Same as {@link completedListLimit} for the ARCHIVED column. */
  archivedListLimit = TASKS2_TERMINAL_PAGE_SIZE;
  isLoadingMoreArchived = false;
  selectedTask: ITask | null = null;
  searchQuery = '';
  /** True while refetching the task list after crossing search-on/search-off terminal-cap boundary. */
  searchTerminalListReloadBusy = false;
  filterTags: ITag[] = [];
  filterUsers: IUser[] = [];
  dueFilter: 'all' | 'today' | 'week' = 'all';
  priorityFilter: TaskPriority[] = [];
  createTaskInitialStatus: TaskStatus | null = null;
  createTaskInitialDate: Date | null = null;

  get task2Filter(): TaskFilterDto {
    return this.$store.getters['PreferencesModule/getTask2Filter'];
  }

  get viewMode(): 'list' | 'kanban' | 'calendar' {
    return this.$store.getters['PreferencesModule/getTaskView'];
  }

  set viewMode(value: 'list' | 'kanban' | 'calendar') {
    this.$store.dispatch(`PreferencesModule/${PreferencesAction.setTaskView}`, value);
  }

  get showDemo(): boolean {
    return this.$store.getters['PreferencesModule/getShowDemo'];
  }

  /** Matches POST /task/list filters so reorder validates against the same task set. */
  get taskReorderContext(): Pick<
    ReorderTasksDto,
    | 'showDemo'
    | 'tags'
    | 'users'
    | 'priority'
    | 'backoffice'
    | 'important'
    | 'completedListLimit'
    | 'archivedListLimit'
  > {
    const tf = this.task2Filter;
    const ctx: Pick<
      ReorderTasksDto,
      | 'showDemo'
      | 'tags'
      | 'users'
      | 'priority'
      | 'backoffice'
      | 'important'
      | 'completedListLimit'
      | 'archivedListLimit'
    > = {
      showDemo: this.showDemo,
      tags: tf.tags,
      users: tf.users,
      priority: tf.priority,
      backoffice: tf.backoffice,
      important: tf.important,
    };
    if (!this.searchRequiresUncappedTerminalLists) {
      ctx.completedListLimit = this.completedListLimit;
      ctx.archivedListLimit = this.archivedListLimit;
    }
    return ctx;
  }

  /**
   * Client-side search only scans loaded rows; COMPLETED/ARCHIVED are normally capped — omit caps on the list API while searching so matches aren’t missing until “load more”.
   */
  get searchRequiresUncappedTerminalLists(): boolean {
    return this.searchQuery.trim().length > 1;
  }

  get showLoadMoreCompleted(): boolean {
    if (this.searchRequiresUncappedTerminalLists) return false;
    const loaded = this.tasks.filter((t) => t.status === TaskStatus.COMPLETED).length;
    return loaded >= this.completedListLimit;
  }

  get showLoadMoreArchived(): boolean {
    if (this.searchRequiresUncappedTerminalLists) return false;
    const loaded = this.tasks.filter((t) => t.status === TaskStatus.ARCHIVED).length;
    return loaded >= this.archivedListLimit;
  }

  get filteredTasks(): ITask[] {
    const hasSearch = this.searchQuery.trim().length > 1;
    const hasTagFilter = this.filterTags.length > 0;
    const hasUserFilter = this.filterUsers.length > 0;
    const hasPriorityFilter = this.priorityFilter.length > 0;
    const hasDueFilter = this.dueFilter !== 'all';

    if (!hasSearch && !hasTagFilter && !hasUserFilter && !hasPriorityFilter && !hasDueFilter) {
      return this.tasks;
    }

    let filtered = [...this.tasks];

    if (hasSearch) {
      const searchLower = this.searchQuery.trim().toLowerCase();
      filtered = filtered.filter((task: ITask) => {
        const searchableText = [
          task.title || '',
          task.description || '',
          ...(task.tags || []).map((t) => t.name || ''),
          ...(task.users || []).map((u) => `${u.firstname || ''} ${u.lastname || ''}`),
        ]
          .join(' ')
          .toLowerCase();
        return searchableText.includes(searchLower);
      });
    }

    if (hasTagFilter) {
      const tagIds = new Set(this.filterTags.map((t) => t.id));
      filtered = filtered.filter((task: ITask) => (task.tags || []).some((t) => tagIds.has(t.id)));
    }

    if (hasUserFilter) {
      const userIds = new Set(this.filterUsers.map((u) => u.id));
      filtered = filtered.filter((task: ITask) =>
        (task.users || []).some((u) => userIds.has(u.id)),
      );
    }

    if (hasPriorityFilter) {
      const prioritySet = new Set(this.priorityFilter);
      filtered = filtered.filter((task: ITask) => {
        const taskPriority = task.priority ?? TaskPriority.NO_PRIORITY;
        return prioritySet.has(taskPriority);
      });
    }

    if (hasDueFilter) {
      const today = dayjs().startOf('day');
      const weekEnd = dayjs().endOf('week');
      filtered = filtered.filter((task: ITask) => {
        const due = task.dueAt ? dayjs(task.dueAt) : null;
        if (!due) return false;
        if (this.dueFilter === 'today') {
          return due.isSame(today, 'day');
        }
        if (this.dueFilter === 'week') {
          return !due.isBefore(today) && !due.isAfter(weekEnd);
        }
        return true;
      });
    }

    return filtered;
  }

  @Watch('searchQuery')
  async watchSearchQueryForTerminalListFetch() {
    const next = this.searchRequiresUncappedTerminalLists;
    if (next === this.prevSearchUncappedTerminalFetch) return;
    this.prevSearchUncappedTerminalFetch = next;
    this.searchTerminalListReloadBusy = true;
    try {
      await this.loadTasks({ silent: true });
    } catch {
      /* loadTasks already surfaces errors */
    } finally {
      this.searchTerminalListReloadBusy = false;
    }
  }

  @Watch('task2Filter', { deep: true })
  async watchTask2Filter() {
    this.completedListLimit = TASKS2_TERMINAL_PAGE_SIZE;
    this.archivedListLimit = TASKS2_TERMINAL_PAGE_SIZE;
    await this.loadTasks();
  }

  async mounted() {
    this.filter = { ...this.task2Filter };
    this.filterTags = [...(this.task2Filter.tags || [])];
    this.filterUsers = [...(this.task2Filter.users || [])];
    this.restorePriorityFilter();
    this.restoreDueFilter();
    this.filter.status = [...TASKS2_LIST_STATUSES];
    await this.loadTasks();
    await this.handleRouteForTask();
  }

  @Watch('$route')
  async onRouteChanged() {
    await this.handleRouteForTask();
  }

  async onPriorityFilterChanged(priorities: TaskPriority[]) {
    this.priorityFilter = [...priorities];
    this.persistPriorityFilter();
  }

  async onTagsFilterChanged(tags: ITag[]) {
    this.filterTags = [...tags];
    await this.persistToolbarFilters();
  }

  async onUsersFilterChanged(users: IUser[]) {
    this.filterUsers = [...users];
    await this.persistToolbarFilters();
  }

  onDueFilterChanged(due: 'all' | 'today' | 'week') {
    this.dueFilter = due;
    this.persistDueFilter();
  }

  private restoreDueFilter() {
    const raw = window.localStorage.getItem(Tasks.DUE_FILTER_STORAGE_KEY);
    if (raw === 'today' || raw === 'week' || raw === 'all') {
      this.dueFilter = raw;
    }
  }

  private restorePriorityFilter() {
    const raw = window.localStorage.getItem(Tasks.PRIORITY_FILTER_STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as TaskPriority[];
      if (Array.isArray(parsed)) {
        this.priorityFilter = parsed.filter((p) => Object.values(TaskPriority).includes(p));
      }
    } catch {
      this.priorityFilter = [];
    }
  }

  private persistDueFilter() {
    window.localStorage.setItem(Tasks.DUE_FILTER_STORAGE_KEY, this.dueFilter);
  }

  private persistPriorityFilter() {
    window.localStorage.setItem(
      Tasks.PRIORITY_FILTER_STORAGE_KEY,
      JSON.stringify(this.priorityFilter),
    );
  }

  private async persistToolbarFilters() {
    const nextFilter: TaskFilterDto = {
      ...this.task2Filter,
      tags: [...this.filterTags],
      users: [...this.filterUsers],
    };
    await this.$store.dispatch(`PreferencesModule/${PreferencesAction.setTask2Filter}`, nextFilter);
  }

  async handleRouteForTask() {
    const id = this.$route.params.id;
    if (!id) return;

    let task = this.tasks.find((t) => t.id === id) || null;
    if (!task) {
      try {
        const res = await TaskApi.getTasks({
          agency: this.user.agency,
          status: [...TASKS2_LIST_STATUSES],
          taskIds: [id],
        });
        task = res.data?.[0] || null;
      } catch (e) {
        this.$toastr.e('Aufgabe nicht gefunden');
        this.$router.replace({ name: 'tasks2' });
        return;
      }
    }

    this.selectedTask = task;
    this.$nextTick(() => {
      (this.$refs['edit-task-modal'] as any)?.$refs?.['edit-task']?.show();
    });
  }

  openTaskById = async (taskOrId: ITask | string) => {
    let task: ITask | null =
      typeof taskOrId === 'object' ? taskOrId : this.tasks.find((t) => t.id === taskOrId) || null;
    if (!task) {
      const id = typeof taskOrId === 'string' ? taskOrId : taskOrId.id;
      try {
        const res = await TaskApi.getTasks({
          agency: this.user.agency,
          status: [...TASKS2_LIST_STATUSES],
          taskIds: [id],
          showDemo: this.showDemo,
        });
        task = res.data?.[0] || null;
      } catch (e) {
        this.$toastr.e('Aufgabe nicht gefunden');
        return;
      }
    }
    if (task) {
      this.selectedTask = task;
      this.$nextTick(() => {
        (this.$refs['edit-task-modal'] as any)?.$refs?.['edit-task']?.show();
      });
      if (this.$route.name !== 'tasks2.edit' || this.$route.params.id !== task.id) {
        this.$router.push({ name: 'tasks2.edit', params: { id: task.id } });
      }
    }
  };

  onModalClosed() {
    this.selectedTask = null;
    if (this.$route.name === 'tasks2.edit') {
      this.$router.replace({ name: 'tasks2' });
    }
  }

  async onTaskDeleted() {
    this.selectedTask = null;
    await this.reload();
    if (this.$route.name === 'tasks2.edit') {
      this.$router.replace({ name: 'tasks2' });
    }
  }

  async loadTasks(options?: {
    loadingMoreCompleted?: boolean;
    loadingMoreArchived?: boolean;
    silent?: boolean;
  }) {
    const loadingMoreCompleted = options?.loadingMoreCompleted === true;
    const loadingMoreArchived = options?.loadingMoreArchived === true;
    const silent = options?.silent === true;
    try {
      if (loadingMoreCompleted) {
        this.isLoadingMoreCompleted = true;
      }
      if (loadingMoreArchived) {
        this.isLoadingMoreArchived = true;
      }
      if (!loadingMoreCompleted && !loadingMoreArchived && !silent) {
        this.isLoading = true;
      }
      this.filter = {
        ...this.task2Filter,
        status: [...TASKS2_LIST_STATUSES],
        taskIds: undefined,
        showDemo: this.showDemo,
      };

      const listPayload: TaskFilterDto = {
        ...this.filter,
        agency: this.user.agency,
        summary: true,
      };
      if (!this.searchRequiresUncappedTerminalLists) {
        listPayload.completedListLimit = this.completedListLimit;
        listPayload.archivedListLimit = this.archivedListLimit;
      }

      const response = await TaskApi.getTasks(listPayload);
      this.tasks = response.data || [];
    } catch (e) {
      this.$toastr.e(
        'Beim Laden der Tasks ist etwas schiefgegangen. Wir haben den Fehler erfasst und kümmern uns schnellstmöglich.',
      );
      throw e;
    } finally {
      if (loadingMoreCompleted) {
        this.isLoadingMoreCompleted = false;
      }
      if (loadingMoreArchived) {
        this.isLoadingMoreArchived = false;
      }
      if (!loadingMoreCompleted && !loadingMoreArchived) {
        this.isLoading = false;
      }
    }
  }

  async loadMoreCompletedTasks() {
    const previousLimit = this.completedListLimit;
    this.completedListLimit += TASKS2_TERMINAL_PAGE_SIZE;
    try {
      await this.loadTasks({ loadingMoreCompleted: true });
    } catch {
      this.completedListLimit = previousLimit;
    }
  }

  async loadMoreArchivedTasks() {
    const previousLimit = this.archivedListLimit;
    this.archivedListLimit += TASKS2_TERMINAL_PAGE_SIZE;
    try {
      await this.loadTasks({ loadingMoreArchived: true });
    } catch {
      this.archivedListLimit = previousLimit;
    }
  }

  /** Refresh list without full-page / kanban loading UI (after drag, save, delete, etc.). */
  async reload() {
    await this.loadTasks({ silent: true });
  }

  openCreateTaskModal() {
    this.createTaskInitialStatus = null;
    this.createTaskInitialDate = null;
    this.$bvModal.show('create-task');
  }

  openCreateTaskModalWithStatus(status: TaskStatus) {
    this.createTaskInitialStatus = status;
    this.createTaskInitialDate = null;
    (this.$refs['create-task-modal'] as any).showWithStatus(status);
  }

  openCreateTaskModalWithDate(date: Date) {
    (this.$refs['create-task-modal'] as any).showWithDate(date);
  }
}

export default Tasks;
